import { useState } from 'react';
import { FormattedMessage, defineMessages } from '@alltrails/shared/react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import PlusBadge from '@alltrails/shared/denali/components/PlusBadge';
import Play from '@alltrails/shared/icons/Play';
import useToggle from '@alltrails/shared/hooks/useToggle';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import logFlyoverStartButtonTapped from '@alltrails/analytics/events/logFlyoverStartButtonTapped';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import FlyoverSource from '@alltrails/analytics/enums/FlyoverSource';
import SignupModal from 'components/SignupModal';
import useMobileWidth from 'hooks/useMobileWidth';
import useUser from 'hooks/useUser';
import FlyoverUpsell from 'components/FlyoverUpsell';
import { Trail } from 'types/Trails';
import User from '@alltrails/shared/types/User';
import useExperiment from 'hooks/useExperiment';
import CheckoutModal from 'components/CheckoutModal';
import FlyoverUpsell2 from 'components/FlyoverUpsell2';
import * as styles from './styles.module.scss';

const messages = defineMessages({
  PREVIEW_TRAIL: {
    defaultMessage: 'Preview trail'
  }
});

type FlyoverEntryProps = {
  exploreFlag?: boolean;
  exactExplorePath: string;
  initializingFlyover?: boolean;
  onEntryClick?: () => void;
  trail: Trail;
};

const FlyoverEntry = ({ exploreFlag, exactExplorePath, initializingFlyover = false, onEntryClick, trail }: FlyoverEntryProps): JSX.Element => {
  const {
    formattedDefaultMessages: { PREVIEW_TRAIL }
  } = useFormatMessage(messages);

  const user = useUser();
  const isMobileWidth = useMobileWidth();
  const experiment = useExperiment('growth-on-page-web-checkout');

  const [isSignupModalOpen, toggleSignupModal] = useToggle(false);
  const [isFlyoverUpsellModalOpen, toggleFlyoverUpsellModal] = useToggle(false);
  const [isFlyoverUpsell2ModalOpen, toggleFlyoverUpsell2Modal] = useToggle(false);
  const [showOfflineError, setShowOfflineError] = useState(false);
  const [isCheckoutModalOpen, toggleCheckoutModal] = useToggle();

  const flyoverParam = 'initFlyover=true';
  const flyoverHref = exactExplorePath.indexOf('?') === -1 ? `${exactExplorePath}?${flyoverParam}` : `${exactExplorePath}&${flyoverParam}`;
  const linkInfo = !!user?.pro && !exploreFlag ? { href: flyoverHref } : null;

  const onFlyoverEntryClick = (ev: React.MouseEvent) => {
    logFlyoverStartButtonTapped({ source: exploreFlag ? FlyoverSource.MapDetails : FlyoverSource.TrailDetails, trail_id: trail.trail_id });
    setShowOfflineError(false);
    if (!window.navigator.onLine) {
      ev.preventDefault();
      setShowOfflineError(true);
      return;
    }

    if (experiment?.value === 'treatment') {
      if (!user.pro) {
        toggleFlyoverUpsell2Modal();
      } else {
        if (exploreFlag) {
          window.history.pushState(null, '');
        }
        onEntryClick?.();
      }
    } else if (!user) {
      toggleSignupModal();
    } else if (!user.pro) {
      toggleFlyoverUpsellModal();
    } else {
      if (exploreFlag) {
        window.history.pushState(null, '');
      }
      onEntryClick?.();
    }
  };

  if (trail.length === 0 && trail.elevation_gain === 0) {
    return null;
  }

  return (
    <div className={styles.flyoverContainer}>
      <CheckoutModal isOpen={isCheckoutModalOpen} onRequestClose={toggleCheckoutModal} returnTo={flyoverHref} />
      <FlyoverUpsell2
        isOpen={isFlyoverUpsell2ModalOpen}
        onClick={() => {
          toggleFlyoverUpsell2Modal();

          if (!user) {
            toggleSignupModal();
          } else {
            // the flyover modal only appears if you're not pro so a pro check here isn't necessary
            toggleCheckoutModal();
          }
        }}
        onRequestClose={toggleFlyoverUpsell2Modal}
      />
      <FlyoverUpsell flyoverHref={flyoverHref} isOpen={isFlyoverUpsellModalOpen} onRequestClose={toggleFlyoverUpsellModal} />
      <SignupModal
        trigger={CarouselDisplayTrigger.Flyover}
        heading={<FormattedMessage defaultMessage="Access Trail Previews and more with AllTrails+" />}
        isOpen={isSignupModalOpen}
        onRequestClose={toggleSignupModal}
        onSuccess={(user: User) => {
          toggleSignupModal();

          if (!user.pro) {
            if (experiment?.value === 'treatment') {
              toggleCheckoutModal();
            } else {
              toggleFlyoverUpsellModal();
            }
            return;
          }

          if (exploreFlag) {
            onEntryClick();
          } else {
            // redirect the user to explore so we can start the flyover
            window.location.assign(flyoverHref);
          }
        }}
      />
      <div className={styles.buttonEntry}>
        <Button
          icon={{ Component: Play }}
          loading={initializingFlyover}
          linkInfo={linkInfo}
          onClick={onFlyoverEntryClick}
          testId="flyover-entry"
          text={PREVIEW_TRAIL}
          size="md"
          width={isMobileWidth || exploreFlag ? 'full' : 'auto'}
        />
        <PlusBadge className={styles.plusBadge} />
      </div>
      {showOfflineError && (
        <span className={styles.error}>
          <FormattedMessage defaultMessage="We’re unable to  submit your request because you’re offline. Please try again later." />
        </span>
      )}
    </div>
  );
};

export default FlyoverEntry;
